/* eslint-disable new-cap */
import Backbone from 'backbone';
import ko from 'knockout';
import _ from 'underscore';
import config from '@bingads-webui-component-legacy/config/index';
import ViewModel from '@bingads-webui-campaign-legacy/dsa-settings/viewmodel/dsa-settings';
import Language from '@bingads-webui-component-legacy/language-selector/view/index';
import TargetingSourceView from '@bingads-webui-campaign-legacy/auto-targets/view/pagefeed/targeting-source';
import tmpl from '@bingads-webui-campaign-legacy/dsa-settings/template/dsa-settings.pug';

const i18n = window.globalLegacyI18n.get('@bingads-webui/i18n-amd-loader!campaignui/campaign-creation-advanced');
const isDomesticSimplifiedChineseEnabled = _.result(config.get('permissions'), 'IsDomesticSimplifiedChineseEnabled');
const defaultLanguage = isDomesticSimplifiedChineseEnabled ? 'SimplifiedChinese' : 'English';

export default class DSASettings extends Backbone.View {
  initialize({
    campaignId = null,
    DomainName = '',
    DomainLanguage = defaultLanguage,
    Source = 'SystemIndex',
    logActivity = _.noop,
    popupIds = {
      CampaignName: 'pop_BA_CampaignCreate_Settings_CampaignName',
      Language: 'pop_BA_CampaignCreate_Settings_AdLanguage',
      MultiLanguage: 'pop_BA_CampaignCreate_Settings_LanguageTargeting',
      TargetingSource: 'pop_BA_DSA_TargetingSource',
    },
    disabled = false,
    dynamicDescriptionEnabled = true,
    isMixedDSACampaignRecommendation = false,
  } = {}) {
    this.popupIds = popupIds;
    this.viewModel = new ViewModel({
      campaignId,
      DomainName,
      DomainLanguage,
      Source,
      disabled,
      dynamicDescriptionEnabled,
      isMixedDSACampaignRecommendation,
    });
    this.logActivity = logActivity;
    this.campaignId = campaignId;

    this.viewModel.DomainName.subscribe(() => {
      this.trigger('change');
    });
    this.viewModel.LanguageCode.subscribe(() => {
      this.trigger('change');
    });
    this.viewModel.TargetingSourceSetting.subscribe(() => {
      this.trigger('change');
    });
    if (this.viewModel.isDynamicDescriptionPhase1Enabled()) {
      this.viewModel.isDynamicDescriptionChecked.subscribe(() => {
        this.trigger('change');
      });
    }
  }

  get events() {
    return {
      'blur .dsa-domainname-input': 'onDomainNameChange',
    };
  }

  onDomainNameChange() {
    this.trigger('change.domainName', this.viewModel.DomainName());
  }

  onLanguageChange() {
    this.trigger('change.language', this.viewModel.LanguageCode());
  }

  handleApiErrors(errors) {
    this.viewModel.errors.add(errors);
  }

  setValues({
    DomainName,
    DomainLanguage,
    Source,
    campaignId,
    targetingSourceCampaignId,
    disabled,
    dynamicDescriptionEnabled,
  }) {
    if (campaignId != null) {
      this.viewModel.Id(campaignId);
    }

    if (disabled != null) {
      this.viewModel.disabled(disabled);
      if (this.webSiteLanguage) {
        this.webSiteLanguage.koModel.disabled(disabled);
      }
    }

    if (DomainName != null) {
      this.viewModel.DomainName(DomainName);
    }

    if (DomainLanguage != null) {
      this.viewModel.LanguageCode(DomainLanguage);
      if (this.webSiteLanguage) {
        this.webSiteLanguage.trigger('language:set', this.viewModel.LanguageCode());
      }
    }

    if (Source != null) {
      this.viewModel.TargetingSourceSetting({
        Source,
      });
      if (this.targetingSource) {
        this.targetingSource.trigger('set', {
          source: Source,
          campaignId: targetingSourceCampaignId,
        });
      }

      if (this.viewModel.isDynamicDescriptionPhase1Enabled()) {
        this.viewModel.isDynamicDescriptionChecked(dynamicDescriptionEnabled);
      }
    }
  }

  initWebSiteLanguage() {
    const DSAEnableInFrenchGerman = _.result(config.get('permissions'), 'IsDynamicSearchAdsV1EnabledInFRAndDE');
    const IsEnabledForDSAInCHandNLandITandESandSE = _.result(config.get('permissions'), 'IsEnabledForDSAInCHandNLandITandESandSE');
    const DSAEnabledInPLandPTandTRandGR = _.result(config.get('permissions'), 'IsDSAEnabledInPLandPTandTRandGR');
    const DSAEnabledInDKandFIandNO = _.result(config.get('permissions'), 'IsDSAEnabledInDKandFIandNO');
    const DSAEUPhase2 = _.result(config.get('permissions'), 'dynamic', {}).DSAEUPhase2;
    const DSAEnabledInJapanese = _.result(config.get('permissions'), 'IsDynamicSearchAdsGlobalizationJapanEnabled');
    const IsAdsGlobalizationPhase6Enabled = _.result(config.get('permissions'), 'IsAdsGlobalizationPhase6Enabled');
    const IsSimplifiedChineseLanguageEnabledInMultipleLanguage = _.result(config.get('permissions'), 'IsSimplifiedChineseLanguageEnabledInMultipleLanguage');
    const IsDomesticSimplifiedChineseEnabled = _.result(config.get('permissions'), 'IsDomesticSimplifiedChineseEnabled');
    const IsAdsGlobalizationPhase9DSAEnabled = _.result(config.get('permissions'), 'dynamic', {}).AdsGlobalizationPhase9DSA;
    const IsAdsGlobalizationPhase9Enabled = _.result(config.get('permissions'), 'IsAdsGlobalizationPhase9Enabled');
    const IsAdsGlobalizationPhase9VIEnabled = _.result(config.get('permissions'), 'IsAdsGlobalizationPhase9VIEnabled');
    const isDSASupportMultipleLanguages = DSAEnableInFrenchGerman ||
      IsEnabledForDSAInCHandNLandITandESandSE ||
      DSAEnabledInPLandPTandTRandGR ||
      DSAEnabledInDKandFIandNO ||
      DSAEUPhase2 ||
      DSAEnabledInJapanese ||
      IsAdsGlobalizationPhase6Enabled ||
      IsSimplifiedChineseLanguageEnabledInMultipleLanguage ||
      IsAdsGlobalizationPhase9Enabled ||
      IsAdsGlobalizationPhase9DSAEnabled ||
      IsAdsGlobalizationPhase9VIEnabled;

    if (isDSASupportMultipleLanguages) {
      let languages = ['English'];

      if (DSAEnableInFrenchGerman) {
        languages = languages.concat(['French', 'German']);
      }

      if (IsEnabledForDSAInCHandNLandITandESandSE) {
        languages = languages.concat(['Spanish', 'Italian', 'Dutch', 'Swedish']);
      }

      if (DSAEnabledInPLandPTandTRandGR) {
        // Now only PL is supported for now, will add others later
        languages = languages.concat([
          'Polish',
          'Portuguese',
          'Czech',
          'Romanian',
          'Hungarian',
          'Greek',
          'Slovak',
          'Bulgarian',
          'Croatian',
          'Lithuanian',
          'Slovenian',
          'Estonian',
          'Latvian',
          'Maltese',
        ]);
      }

      if (DSAEnabledInDKandFIandNO) {
        languages = languages.concat(['Danish', 'Finnish', 'Norwegian']);
      }

      if (DSAEUPhase2) {
        languages = languages.concat(['Turkish', 'Serbian', 'Bosnian', 'Albanian', 'Macedonian', 'Icelandic']);
      }

      if (DSAEnabledInJapanese) {
        languages = languages.concat(['Japanese']);
      }

      if (IsSimplifiedChineseLanguageEnabledInMultipleLanguage) {
        languages = languages.concat(['SimplifiedChinese']);
      }

      if (IsAdsGlobalizationPhase6Enabled) {
        languages = languages.concat(['Hebrew', 'Arabic', 'Russian', 'TraditionalChinese']);
      }

      if (IsAdsGlobalizationPhase9DSAEnabled) {
        languages = languages.concat(['Hindi', 'Malay', 'Thai', 'Indonesian', 'Tagalog']);
      }

      if (IsAdsGlobalizationPhase9VIEnabled) {
        languages = languages.concat(['Vietnamese']);
      }

      if (IsDomesticSimplifiedChineseEnabled) {
        languages = ['SimplifiedChinese'];
      }

      this.webSiteLanguage = new Language({
        i18nStrings: {
          Description: '',
          SaveWarning: '',
        },
        language: this.viewModel.LanguageCode(),
        languages,
        IsAdsGlobalizationPhase1Enabled: _.result(config.get('permissions'), 'IsAdsGlobalizationPhase1Enabled'),
        IsAdsGlobalizationPhase2Enabled: _.result(config.get('permissions'), 'IsAdsGlobalizationPhase2Enabled'),
        IsAdsGlobalizationPhase6Enabled,
        IsAdsGlobalizationPhase2JapanEnabled: _.result(config.get('permissions'), 'IsAdsGlobalizationPhase2JapanEnabled'),
        enableSimplifiedChinese: IsSimplifiedChineseLanguageEnabledInMultipleLanguage || IsDomesticSimplifiedChineseEnabled,
        IsAdsGlobalizationPhase9Enabled,
        IsAdsGlobalizationPhase9DSAEnabled,
        IsAdsGlobalizationPhase9VIEnabled,
        // TO DO
      });
      this.webSiteLanguage.on('language:changed', (language) => {
        this.viewModel.LanguageCode(language);
        this.logActivity('website language changed');
        this.onLanguageChange();
      }, this);
    }
  }

  initTargetSource() {
    if (this.targetingSource) {
      this.targetingSource.remove();
      this.targetingSource = null;
    }

    if (this.viewModel.isTargetingSourceEnabled()) {
      this.targetingSource = new TargetingSourceView({
        source: this.viewModel.TargetingSourceSetting().Source,
        campaignId: this.campaignId,
        activityLogger: this.options.activityLogger,
      });
      this.targetingSource.on('update', (value) => {
        this.viewModel.TargetingSourceSetting({
          Source: value.source,
        });
      }, this);
    }
  }

  validate() {
    const isTargetingSourceValid = this.targetingSource ? this.targetingSource.validate() : true;

    return this.viewModel.validate() && isTargetingSourceValid;
  }

  render() {
    this.$el.html(tmpl({
      i18n,
      popupIds: this.popupIds,
      isDomesticSimplifiedChineseEnabled,
    }));
    ko.applyBindings(this.viewModel, this.el);

    this.initWebSiteLanguage();
    this.initTargetSource();

    const DSAEnableInFrenchGerman = _.result(config.get('permissions'), 'IsDynamicSearchAdsV1EnabledInFRAndDE');
    const IsEnabledForDSAInCHandNLandITandESandSE = _.result(config.get('permissions'), 'IsEnabledForDSAInCHandNLandITandESandSE');
    const DSAEnabledInPLandPTandTRandGR = _.result(config.get('permissions'), 'IsDSAEnabledInPLandPTandTRandGR');
    const DSAEnabledInDKandFIandNO = _.result(config.get('permissions'), 'IsDSAEnabledInDKandFIandNO');
    const DSAEnabledInJapanese = _.result(config.get('permissions'), 'IsDynamicSearchAdsGlobalizationJapanEnabled');
    const IsSimplifiedChineseLanguageEnabledInMultipleLanguage = _.result(config.get('permissions'), 'IsSimplifiedChineseLanguageEnabledInMultipleLanguage');

    const isDSASupportMultipleLanguages = DSAEnableInFrenchGerman || IsEnabledForDSAInCHandNLandITandESandSE || DSAEnabledInPLandPTandTRandGR || DSAEnabledInDKandFIandNO || DSAEnabledInJapanese || IsSimplifiedChineseLanguageEnabledInMultipleLanguage;

    if (this.campaignId != null && this.options.isCampaignSettingsScenario && this.options.DomainName) {
      const lang = i18n.getString(`Language_${this.viewModel.LanguageCode()}`) || this.viewModel.LanguageCode();

      this.$('.website-language-setting').empty().append(lang);
    } else if (isDSASupportMultipleLanguages) {
      this.$('.website-language-setting').empty().append(this.webSiteLanguage.render().el);
    } // if not in editing and not enable DSA in French and German, it will show English by default

    if (this.viewModel.isTargetingSourceEnabled()) {
      this.$('.dsa-targeting-source').empty().append(this.targetingSource.render().el);
    }
    return this;
  }

  getValues(trimDomainName = false) {
    if (!trimDomainName) {
      return _.extend({}, this.viewModel.toObject(),
        this.viewModel.isTargetingSourceEnabled() ? { Source: this.viewModel.TargetingSourceSetting().Source } : {},
        this.viewModel.isDynamicDescriptionPhase1Enabled() ? { DynamicDescriptionEnabled: this.viewModel.isDynamicDescriptionChecked()} : {});
    }

    const context = {};

    // strip off http(s) and www.
    const domainName = this.viewModel.DomainName();
    let processedDomainName = domainName.trim().toLowerCase();

    processedDomainName = processedDomainName.replace(new RegExp('^http(s)?://'), '');
    processedDomainName = processedDomainName.replace(new RegExp('^w{3}.'), '');
    this.viewModel.DomainName(processedDomainName);
    context.DomainName = this.viewModel.DomainName();
    context.Language = this.viewModel.LanguageCode();
    context.DomainLanguage = this.viewModel.LanguageCode();
    if (this.viewModel.isTargetingSourceEnabled()) {
      context.TargetingSource = this.viewModel.TargetingSourceSetting().Source;
      context.Source = this.viewModel.TargetingSourceSetting().Source;
    }

    if (this.viewModel.isDynamicDescriptionPhase1Enabled()) {
      context.DynamicDescriptionEnabled = this.viewModel.isDynamicDescriptionChecked();
    }

    return context;
  }

  remove() {
    if (this.webSiteLanguage) {
      this.webSiteLanguage.remove();
    }

    if (this.targetingSource) {
      this.targetingSource.remove();
    }

    super.remove();
  }
}
